// src/components/MatrixData.jsx
import { useContext, useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { client } from '../../hooks/Client'
import MatrixDevelopmentBarChart from '../Visualisations/development/MatrixDevelopmentBarChart'
import MatrixDevelopmentLineChart from '../Visualisations/development/MatrixDevelopmentLineChart'
import MatrixDevelopmentPieChart from '../Visualisations/development/MatrixDevelopmentPieChart'
import MatrixMomentBarChart from '../Visualisations/moment/MatrixMomentBarChart'
import MatrixMomentLineChart from '../Visualisations/moment/MatrixMomentLineChart'
import MatrixMomentPieChart from '../Visualisations/moment/MatrixMomentPieChart'
import MatrixDevelopmentCategoricalBarChart from '../Visualisations/development/MatrixDevelopmentCategoricalBarchart'
import MatrixTransitionSankey from '../Visualisations/development/MatrixTransitionSankey'
import { MatrixDistributionBarChart, MatrixDistributionPies } from '../Visualisations/moment/MatrixDistributionCharts'
import spinner from '../../images/spinner-ripple.svg'
import useMatrixData from '../data/useMatrixData'
import useMsiBenchmark from '../../hooks/useMsiBenchmark'
import { resolveColor } from '../../helpers/visualisationDefaults'
import useChartColors from '../../hooks/useChartColors'
import { useEmptyStateContext } from '../../StateManagment/EmptyStateContext'
import ScoreBlock from '../analysis/ScoreBlock'
import MatrixRadarChart from '../Visualisations/development/MatrixRadarChart'
import calculateTotalGrowth from '../../helpers/dataHelpers/calculateTotalGrowth'
import LiveReportRenderContext from '../../StateManagment/LiveReportRenderContext'
import MetricVisualization from '../dataVisualizations/MetricVisualization'
import { matrixTable, categoricalMatrixTable } from '../../helpers/dataHelpers/chartDataTable'
import ChartDataTable from './ChartDataTable'

const MatrixData = ({
  effectId,
  field,
  researchId,
  startDate,
  endDate,
  graphType,
  graphColor,
  projectId,
  colors,
  personaId,
  filterByResearchId,
  type,
  colorPalette,
  colorOverrides,
  numberFontSize,
  numberTextAlign,
  numberFontWeight,
  dashboardOptions,
  onTotalResponsesChange,
  infoPopoverContent,
  templateData,
}) => {
  const {
    data,
    rowData,
    rowDefs,
    rawRowDefs,
    lowerScale,
    upperScale,
    totalResponses,
    projectHierarchy,
    loading,
    isCategorical,
    transitionSankey,
    hasPersonaTransitions,
    distribution,
    error,
  } = useMatrixData({ field, researchId, startDate, endDate, personaId, filterByResearchId, templateData })

  // Palette: prefer the prop (passed from Dashboard card / Analysis body),
  // fall back to house-style so the hook also works standalone.
  const { palette, overrides } = useChartColors({ colorPalette, colorOverrides })

  // Build a title → { id, index } lookup from the rowDefs exposed by the hook.
  // Used to attach colors at render time via resolveColor(), so palette or
  // ColorOverrides changes don't require refetching the matrix.
  const titleColorMap = useMemo(() => {
    const map = {}
    ;(rowDefs || []).forEach((row, index) => {
      if (row?.Title) {
        map[row.Title] = resolveColor(row.ID, index, palette, overrides)
      }
    })
    return map
  }, [rowDefs, palette, overrides])

  // Kleur per stelling voor de verdeling. Op de echte stellingen (rawRowDefs),
  // niet op rowDefs: bij een MSI-vraag is dat één synthetische 'Gemiddelde'.
  // AI-CONTEXT: See docs/pitfalls.md#ui-msi-matrix-rowdefs-collapse
  const distributionColorMap = useMemo(() => {
    const map = {}
    ;(rawRowDefs || []).forEach((row, index) => {
      if (row?.Title) map[row.Title] = resolveColor(row.ID, index, palette, overrides)
    })
    return map
  }, [rawRowDefs, palette, overrides])

  // Remap cells to carry the resolved color alongside value/counts/negative,
  // preserving the shape the chart components expect (data[i][rowTitle].color).
  const coloredData = useMemo(() => {
    return (data || []).map(moment => {
      const out = { name: moment.name }
      Object.keys(moment).forEach(key => {
        if (key === 'name') return
        const cell = moment[key]
        if (cell && typeof cell === 'object') {
          out[key] = { ...cell, color: titleColorMap[key] || null }
        } else {
          out[key] = cell
        }
      })
      return out
    })
  }, [data, titleColorMap])

  const history = useHistory()
  const onResponsesClick = field
    ? () => history.push(`/${client}/responsedetails/${field}`)
    : undefined

  // Cross-company benchmark, fetched once here and shared with the charts and
  // the ScoreBlock.
  const { setSankeyAvailable } = useEmptyStateContext()
  // Live reports render the chart without the internal ScoreBlock (it's
  // steering info); dashboard/Analyse keep it.
  const inLiveReport = useContext(LiveReportRenderContext)
  const { benchmark, loading: benchmarkLoading, benchmarkUnavailableReason } = useMsiBenchmark(effectId || '')

  useEffect(() => {
    onTotalResponsesChange?.(totalResponses ?? 0);
  }, [totalResponses, onTotalResponsesChange]);

  // Publish sankey availability so the card's graph-type picker can hide the
  // sankey icon when the data isn't persona-linked across moments.
  useEffect(() => {
    setSankeyAvailable?.(hasPersonaTransitions);
  }, [hasPersonaTransitions, setSankeyAvailable]);

  if (loading) return <img src={spinner} alt="loading" />
  if (error)   return <p style={{ color: 'crimson' }}>Fout bij laden van matrixdata.</p>

  // 'Verdeling': per stelling hoe vaak elk antwoord gekozen is. Vóór de tak
  // voor tekstantwoorden, want juist die vragen (zoals 'Mee eens') hebben dit
  // nodig. Updateverzoek Drenthe beweegt, 2026-10-02.
  // De cijfertabel tekent de grafiek zelf, per meetmoment.
  if (graphType === 'distribution' || graphType === 'distribution-pie') {
    return (
      <>
        {graphType === 'distribution'
          ? <MatrixDistributionBarChart distribution={distribution} rowColors={distributionColorMap} />
          : <MatrixDistributionPies distribution={distribution} />}
        {dashboardOptions}
      </>
    )
  }

  if (isCategorical) {
    return (
      <>
        <MatrixDevelopmentCategoricalBarChart
          data={coloredData}
          totalResponses={totalResponses}
        />
        <ChartDataTable table={categoricalMatrixTable(coloredData)} />
        {dashboardOptions}
      </>
    )
  }

  // Total-growth view: a single big "+X%" number instead of a chart.
  // Only the live-report widget toggle sets this graphType.
  if (graphType === 'total-growth') {
    const titles = coloredData.length
      ? Object.keys(coloredData[0]).filter(k => k !== 'name')
      : []
    const growth = Math.round(
      calculateTotalGrowth(coloredData, titles, upperScale, lowerScale) * 10
    ) / 10
    const metricColor = overrides?.primary
    const valueStyle = (metricColor || numberFontSize || numberFontWeight)
      ? {
          ...(metricColor ? { color: metricColor } : {}),
          ...(numberFontSize ? { fontSize: numberFontSize } : {}),
          ...(numberFontWeight ? { fontWeight: numberFontWeight } : {}),
        }
      : undefined
    return (
      <>
        <MetricVisualization
          value={growth}
          formatValue={(v) => `${v > 0 ? '+' : ''}${v}%`}
          valueStyle={valueStyle}
          align={numberTextAlign}
        />
        {dashboardOptions}
      </>
    )
  }

  const isSingle = coloredData.length === 1
  const chartMap = {
    single: {
      line: (
        <MatrixMomentLineChart
          data={coloredData}
          lowerScale={lowerScale}
          upperScale={upperScale}
          totalResponses={totalResponses}
          projectId={projectId}
          colors={colors}
          projectHierarchy={projectHierarchy}
          effectId={effectId}
          benchmark={benchmark}
          graphColor={graphColor}
        />
      ),
      bar: (
        <MatrixMomentBarChart
          data={coloredData}
          lowerScale={lowerScale}
          upperScale={upperScale}
          totalResponses={totalResponses}
          colors={colors}
          projectHierarchy={projectHierarchy}
          effectId={effectId}
          benchmark={benchmark}
          graphColor={graphColor}
        />
      ),
      pie: <MatrixMomentPieChart data={coloredData} graphColor={graphColor} />,
      radar: (
        <MatrixRadarChart
          rowData={rowData}
          rawRowDefs={rawRowDefs}
          lowerScale={lowerScale}
          upperScale={upperScale}
          graphColor={graphColor}
        />
      ),
      sankey: (
        <MatrixTransitionSankey
          transitionSankey={transitionSankey}
          lowerScale={lowerScale}
          upperScale={upperScale}
          benchmarkGrowth={benchmark?.benchmarkGrowth ?? null}
        />
      ),
    },
    multiple: {
      line: (
        <MatrixDevelopmentLineChart
          data={coloredData}
          lowerScale={lowerScale}
          upperScale={upperScale}
          totalResponses={totalResponses}
          colors={colors}
          projectHierarchy={projectHierarchy}
          effectId={effectId}
          infoPopoverContent={infoPopoverContent}
          fieldId={field}
          benchmark={benchmark}
          benchmarkLoading={benchmarkLoading}
          benchmarkUnavailableReason={benchmarkUnavailableReason}
          onResponsesClick={onResponsesClick}
          graphColor={graphColor}
        />
      ),
      bar: (
        <MatrixDevelopmentBarChart
          data={coloredData}
          lowerScale={lowerScale}
          upperScale={upperScale}
          totalResponses={totalResponses}
          colors={colors}
          projectHierarchy={projectHierarchy}
          effectId={effectId}
          infoPopoverContent={infoPopoverContent}
          fieldId={field}
          benchmark={benchmark}
          benchmarkLoading={benchmarkLoading}
          benchmarkUnavailableReason={benchmarkUnavailableReason}
          onResponsesClick={onResponsesClick}
          graphColor={graphColor}
        />
      ),
      pie: (
        <MatrixDevelopmentPieChart
          data={coloredData}
          lowerScale={lowerScale}
          upperScale={upperScale}
          totalResponses={totalResponses}
          colors={colors}
          projectHierarchy={projectHierarchy}
          effectId={effectId}
          infoPopoverContent={infoPopoverContent}
          fieldId={field}
          benchmark={benchmark}
          benchmarkLoading={benchmarkLoading}
          benchmarkUnavailableReason={benchmarkUnavailableReason}
          onResponsesClick={onResponsesClick}
          graphColor={graphColor}
        />
      ),
      radar: (
        <MatrixRadarChart
          rowData={rowData}
          rawRowDefs={rawRowDefs}
          lowerScale={lowerScale}
          upperScale={upperScale}
          graphColor={graphColor}
        />
      ),
      sankey: (
        <MatrixTransitionSankey
          transitionSankey={transitionSankey}
          lowerScale={lowerScale}
          upperScale={upperScale}
          benchmarkGrowth={benchmark?.benchmarkGrowth ?? null}
        />
      ),
    },
  }

  const mode = isSingle ? 'single' : 'multiple'
  // Sankey needs persona-paired transitions; fall back to the default chart when
  // the picker offered sankey but the data isn't persona-linked across moments.
  const requestedGraphType = graphType || 'bar'
  const effectiveGraphType = requestedGraphType === 'sankey' && !hasPersonaTransitions ? 'bar' : requestedGraphType

  // MSI-effect sankey with a benchmark: the distribution shift in the chart,
  // the benchmark growth in the ScoreBlock above (it's a growth figure with no
  // per-moment score, so it never becomes a chart mark). Needs ≥2 moments.
  // AI-CONTEXT: See docs/features/analytics-card-redesign.md
  const hasMoments = coloredData.length >= 2
  const hasBenchmark = typeof benchmark?.benchmarkGrowth === 'number' && Number.isFinite(benchmark.benchmarkGrowth)
  const useScoreSankey = hasBenchmark && hasMoments && effectiveGraphType === 'sankey'

  if (useScoreSankey) {
    const titles = Object.keys(coloredData[0]).filter(k => k !== 'name')
    const totalGrowth = Math.round(calculateTotalGrowth(coloredData, titles, upperScale, lowerScale) * 10) / 10

    return (
      <>
        {!inLiveReport && (
          <ScoreBlock
            totalGrowth={totalGrowth}
            benchmark={benchmark}
            benchmarkLoading={benchmarkLoading}
            benchmarkUnavailableReason={benchmarkUnavailableReason}
            effectId={effectId}
          />
        )}
        {chartMap[mode].sankey}
        <ChartDataTable table={matrixTable(coloredData)} />
        {dashboardOptions}
      </>
    )
  }

  const chart = chartMap[mode][effectiveGraphType] || null

  return (
    <>
      {chart}
      {/* Cijfers onder de grafiek, alleen op het PDF-vel. Niet bij de
          tekstantwoorden of het groeigetal hierboven: daar zijn het andere
          gegevens. De verdeling heeft zijn eigen tabel. */}
      <ChartDataTable table={matrixTable(coloredData)} />
      {dashboardOptions}
    </>
  )
}

export default MatrixData
